import { FC, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import styled, { useTheme } from 'styled-components/native';

import IconCancel from '../../assets/svg/IconCancel';
import { isWebPlatform } from '../../boot/utils';
import { isPrerender } from '../../utils/deviceInfo';
import RNModal, {
  ModalProps as RNModalProps,
} from '../../utils/packages/reactNativeModal';
import Touchable from '../Touchable';

export interface ModalProps extends Partial<RNModalProps> {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  showCloseBtn?: boolean;
}

const Modal: FC<ModalProps> = props => {
  const {
    isOpen,
    setIsOpen,
    children,
    showCloseBtn = true,
    style,
    ...restProps
  } = props;
  const {
    palette: { neutralDark },
  } = useTheme();

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  if (isPrerender()) {
    return null;
  }

  return (
    <RNModal
      {...restProps}
      isVisible={isOpen}
      scrollOffset={1}
      style={[styles.modalStyle, style]}
      onBackdropPress={closeModal}
      onBackButtonPress={closeModal}
      // Avoid flashing while closing modal
      // https://github.com/react-native-modal/react-native-modal/issues/268
      {...(isWebPlatform ? {} : { backdropTransitionOutTiming: 0 })}
    >
      <S.SafeAreaView>
        <S.Wrapper>
          {children}
          {showCloseBtn && (
            <S.CloseBtn onPress={closeModal} accessibilityRole="button">
              <IconCancel size={16} fillColor={neutralDark} />
            </S.CloseBtn>
          )}
        </S.Wrapper>
      </S.SafeAreaView>
    </RNModal>
  );
};

export default Modal;

const styles = StyleSheet.create({
  modalStyle: {
    justifyContent: 'flex-end',
    margin: 0,
  },
});
const S = {
  SafeAreaView: styled.SafeAreaView`
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    background-color: ${({ theme }) => theme.palette.white};
  `,
  Wrapper: styled.View`
    padding: 20px;
  `,
  CloseBtn: styled(Touchable)`
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 10px;
  `,
};
